import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { PlanUpgradeModal } from '@/components/billing/PlanUpgradeModal';
import { CreditBalanceChip } from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';
import CommandPalette from '@/components/CommandPalette';

import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import GoogleSignup from '@/pages/GoogleSignup';
import Dashboard from '@/pages/Dashboard';
import VendorDashboard from '@/pages/VendorDashboard';
import LandingPage from '@/pages/LandingPage';
import ClientDashboard from '@/pages/ClientDashboard';
import ClientQuotations from '@/pages/ClientQuotations';
import ClientQuotationsSent from '@/pages/ClientQuotationsSent';
import ClientBOQ from '@/pages/ClientBOQ';
import ClientTasks from '@/pages/ClientTasks';
import ClientApprovals from '@/pages/ClientApprovals';
import VendorQuotationsReceived from '@/pages/VendorQuotationsReceived';
import VendorRunningBills from '@/pages/VendorRunningBills';
import BillingPage from '@/pages/BillingPage';
import AdminBillingManager from '@/pages/AdminBillingManager';
import VendorRates from '@/pages/VendorRates';
import VendorChat from '@/pages/VendorChat';
import VendorBOQ from '@/pages/VendorBOQ';
import VendorTasks from '@/pages/VendorTasks';
import VendorSchedule from '@/pages/VendorSchedule';
import Leads from '@/pages/Leads';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import ProjectDashboard from '@/pages/ProjectDashboard';
import Tasks from '@/pages/Tasks';
import Activities from '@/pages/Activities';  // NEW: Vendor-facing work units
import AdminActivities from '@/pages/AdminActivities';  // NEW: Admin management of vendor activities
import AdminActiveSessions from '@/pages/AdminActiveSessions';  // iter-338r: live presence dashboard
import VendorQuotationCreate from '@/pages/VendorQuotationCreate';  // NEW: Vendor creates quotation from rate card
import Users from '@/pages/Users';
import Documents from '@/pages/Documents';
import Payments from '@/pages/Payments';
import BOQWorksheets from '@/pages/BOQWorksheets';
import Invoices from '@/pages/Invoices';
import Quotations from '@/pages/Quotations';
import Products from '@/pages/Products';
import MaterialsNew from '@/pages/MaterialsNew';
import PurchaseOrders from '@/pages/PurchaseOrdersNew';
import Schedule from '@/pages/Schedule';
import AIBudgetEstimation from '@/pages/AIBudgetEstimation';
import Reports from '@/pages/Reports';
import LegalPages from '@/pages/LegalPages';
import Settings from '@/pages/Settings';
import VendorSettings from '@/pages/VendorSettings';
import AdminVendorRates from '@/pages/AdminVendorRates';
import NotificationPreferences from '@/pages/NotificationPreferences';
import VerifyEmail from '@/pages/VerifyEmail';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import ResendVerification from '@/pages/ResendVerification';
import FullCalendar from '@/pages/FullCalendar';  // NEW: Full Calendar page
import GanttChart from '@/pages/GanttChart';  // P1: Gantt Chart with dependencies
import VendorCatalogues from '@/pages/VendorCatalogues';
import BOQCategoryLibrary from '@/pages/BOQCategoryLibrary';  // BOQ Category Management
// iter-360: Sponsored Brand Placement admin.
import SponsorAdmin from '@/pages/SponsorAdmin';
import SponsorAnalytics from '@/pages/SponsorAnalytics';
import AIPresetLibraryPage from '@/pages/AIPresetLibraryPage';  // iter-338z5: Reusable AI Task presets
import PublicBOQView from '@/pages/PublicBOQView';  // NEW: Public BOQ view for temporary links
import BOQComments from '@/pages/BOQComments';  // NEW: BOQ Comments management
import BOQEditPage from '@/pages/BOQEditPage';  // NEW: Standalone BOQ edit page (new tab)
import WardrobeCalculator from '@/pages/WardrobeCalculator';  // Estimators: Wardrobe/Storage Calculator
import EstimateCompare from '@/pages/EstimateCompare';  // Compare Variants (Good/Better/Best)
import DiscountRulesPage from '@/pages/DiscountRulesPage';  // Admin Discount Rules Engine
import BOQEmailPage from '@/pages/BOQEmailPage';  // NEW: BOQ email compose page (new tab)
import RunningBillsPage from '@/pages/RunningBillsPage';  // iter-326c: Running Bills list + detail
import RunningBillPublicView from '@/pages/RunningBillPublicView';  // iter-326c: client acceptance view
import VendorQuoteSubmission from '@/pages/VendorQuoteSubmission';  // NEW: Public vendor quote submission
import PublicProjectStatus from '@/pages/PublicProjectStatus';  // iter-386u: Public client project status link
import PublicVendorSubmit from '@/pages/PublicVendorSubmit';  // iter-312: Ad-hoc vendor submission link
import PublicGanttView from '@/pages/PublicGanttView';  // iter-338k: Public Gantt link
import AdminDataHealth from '@/pages/AdminDataHealth';  // iter-312i: Data Health dashboard
import CompareVendorQuotes from '@/pages/CompareVendorQuotes';  // NEW: Compare vendor quotes
import ClientPaymentHistory from '@/pages/ClientPaymentHistory';  // NEW: Client payment ledger
import VendorPaymentHistory from '@/pages/VendorPaymentHistory';  // NEW: Vendor payment ledger
import VendorProjectLedger from '@/pages/VendorProjectLedger'; // iter-386u-p: vendor-scoped project RA Bill / ledger
import VendorProjectLedgerIndex from '@/pages/VendorProjectLedgerIndex'; // iter-386u-t: landing page for sidebar link
import PDFTemplates from '@/pages/PDFTemplates';  // NEW: PDF Template customization
import EmailTemplates from '@/pages/EmailTemplates';  // NEW: Email Template customization
import VendorDetail from '@/pages/VendorDetail';  // Vendor directory with detail panel
import VendorCompare from '@/pages/VendorCompare';  // Vendor comparison dashboard
import ClientsList from '@/pages/ClientsList';  // Dedicated clients page
import VendorsLabourList from '@/pages/VendorsLabourList';  // Dedicated vendors & labour page
import PerformanceDashboard from '@/pages/PerformanceDashboard';  // API Performance monitoring
import ClientDetail from '@/pages/ClientDetail';  // Client directory with detail panel
import ProjectAccounts from '@/pages/ProjectAccounts';  // Project accounts summary
import VendorSubmissions from '@/pages/VendorSubmissions';  // iter-309 Vendor Submissions

// Always use relative /api URLs to avoid cross-origin issues.
// Kubernetes ingress routes /api/* to the backend on both preview and production.
const API = '/api';

// iter-312k: Runtime URL self-heal.
// Some deployed bundles got built with a stale REACT_APP_BACKEND_URL (e.g.
// "https://smartexec-4.emergent.host" left over from an earlier K8s service
// hostname). When that URL goes dead, every API call from the browser fails
// with net::ERR_FAILED. Per-page modules use
//   const API = `${process.env.REACT_APP_BACKEND_URL}/api`
// so they're locked to that bad host at build time.
//
// To make the frontend resilient WITHOUT rebuilding, we install an axios
// request interceptor that rewrites any absolute URL whose host doesn't match
// the current page origin into a relative /api path served by the K8s ingress
// of whatever domain the user actually loaded.
axios.interceptors.request.use((config) => {
  try {
    if (typeof config.url === 'string' && /^https?:\/\//i.test(config.url)) {
      const parsed = new URL(config.url);
      const currentOrigin = (typeof window !== 'undefined' && window.location)
        ? window.location.origin
        : '';
      if (currentOrigin && parsed.origin !== currentOrigin) {
        // Same backend topology in every env: /api/* is served from the same
        // host that serves the frontend. So just keep the path.
        config.url = parsed.pathname + parsed.search + parsed.hash;
      }
    }
  } catch {/* ignore */}
  return config;
});

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  // iter-371 fix — NEVER attach the logged-in admin's bearer token to
  // `/api/public/*` endpoints. These are meant to be viewed as an anonymous
  // client / vendor. Attaching the admin token was silently bypassing the
  // OTP gate + view-limit cap whenever the admin previewed their own link
  // in the same browser they were logged into.
  //
  // iter-371-hotfix2 — EXCEPTION: when the URL carries `admin_preview=1`
  // the admin is explicitly asking to bypass the gate (via the
  // "Preview as Admin" button). In that case we DO need to attach the
  // bearer token so the backend can verify the admin role.
  const url = (config?.url || '').toLowerCase();
  const isPublicRoute = url.includes('/api/public/') || url.startsWith('/public/');
  const wantsAdminPreview = url.includes('admin_preview=1') || url.includes('admin_preview=true');
  const token = localStorage.getItem('token');
  if (token && (!isPublicRoute || wantsAdminPreview)) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (isPublicRoute && config.headers) {
    // Make sure no stale Authorization header rides along
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  return config;
});

// Normalize any Pydantic / FastAPI error "detail" field to a plain string
// so that downstream `toast.error(err.response?.data?.detail || '...')` calls
// never receive a raw array / object which would crash React (Objects are not
// valid as a React child).
const stringifyApiDetail = (detail) => {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (!d || typeof d !== 'object') return String(d);
        const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        const msg = d.msg || d.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join('; ');
  }
  if (typeof detail === 'object') {
    if (detail.msg || detail.message) return detail.msg || detail.message;
    try { return JSON.stringify(detail); } catch { return String(detail); }
  }
  return String(detail);
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Flatten detail BEFORE any handler reads it
    if (error.response?.data && typeof error.response.data === 'object' && 'detail' in error.response.data) {
      error.response.data.detail = stringifyApiDetail(error.response.data.detail);
    }
    if (error.response?.status === 401 ||
        (error.response?.status === 403 && error.response?.data?.detail === 'Not authenticated')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Protected Route Component with role refresh
const ProtectedRoute = ({ children, allowedRoles }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    // Refresh user data on mount to catch role changes
    const refreshUserData = async () => {
      if (!token || isRefreshing) return;
      
      setIsRefreshing(true);
      try {
        const response = await axios.get(`${API}/auth/me`);
        const freshUser = response.data;
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Check if role has changed
        if (storedUser.role !== freshUser.role) {
          console.log('Role changed from', storedUser.role, 'to', freshUser.role);
          localStorage.setItem('user', JSON.stringify(freshUser));
          toast.info(`Your role has been updated to ${freshUser.role}. Refreshing...`, { duration: 3000 });
          // Force reload to apply new permissions
          setTimeout(() => window.location.reload(), 1500);
        }
      } catch (error) {
        // If token is invalid, the interceptor will handle logout
        console.error('Failed to refresh user data:', error);
      } finally {
        setIsRefreshing(false);
      }
    };
    
    refreshUserData();

    // iter-386u-ah — Netflix rule: register this browser as an active device
    // and pick up any evictions (oldest sessions logged out on other tabs).
    const registerSession = async () => {
      if (!token) return;
      try {
        let deviceToken = localStorage.getItem('bc_device_token');
        if (!deviceToken) {
          deviceToken = 'dev_' + Math.random().toString(36).slice(2) + '_' + Date.now().toString(36);
          localStorage.setItem('bc_device_token', deviceToken);
        }
        const label = `${navigator.platform || 'device'} · ${(navigator.userAgent || '').split(' ').slice(-1)[0] || 'browser'}`;
        const r = await axios.post(`${API}/sessions/register`, { device_token: deviceToken, device_label: label });
        const revoked = r.data?.revoked || [];
        if (revoked.length) {
          toast('Signed out on another device to keep you under your plan limit.', {
            description: `Devices affected: ${revoked.length}. Upgrade to add more.`,
            duration: 6000,
          });
        }
      } catch (e) { /* silent — endpoint optional */ }
    };
    registerSession();
  }, [token]);
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  // iter-386u-x — role gating. If allowedRoles is passed and the user's
  // role is not included, bounce them home (their dashboard).
  if (allowedRoles && allowedRoles.length > 0) {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
  }
  return children;
};

// Role-based dashboard routing component
const RoleBasedDashboard = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Vendors and Labour get their dedicated dashboard
  if (user.role === 'vendor' || user.role === 'labour') {
    return <VendorDashboard />;
  }
  // Clients get their dedicated dashboard
  if (user.role === 'client') {
    return <ClientDashboard />;
  }
  // Everyone else gets the main dashboard
  return <Dashboard />;
};

// Logged-out visitors see the marketing landing page; signed-in users go straight to their dashboard.
const HomeRoute = () => (localStorage.getItem('token')
  ? <ProtectedRoute><RoleBasedDashboard /></ProtectedRoute>
  : <LandingPage />);

function App() {
  return (
    <ThemeProvider>
      <WebSocketProvider>
      <div className="app-container">
        <BrowserRouter>
          <CommandPalette />
          <Routes>
            {/* Public routes - Legal pages for Razorpay compliance */}
            <Route path="/legal/:page" element={<LegalPages />} />
            
            {/* Public auth routes - Email verification and password reset */}
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
          
          {/* Public routes (no auth required) */}
          <Route path="/public/boq/:token" element={<PublicBOQView />} />
          <Route path="/public/running-bill/:token" element={<RunningBillPublicView />} />
          <Route path="/vendor-quote/:token" element={<VendorQuoteSubmission />} />
          <Route path="/public/project-status/:token" element={<PublicProjectStatus />} />
          <Route path="/p/vendor-submit/:token" element={<PublicVendorSubmit />} />
          <Route path="/p/gantt/:token" element={<PublicGanttView />} />
          
          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          {/* iter-386u-ap — public signup route; renders Login with signup mode default */}
          <Route path="/signup" element={<Login signupMode />} />
          {/* iter-318: Emergent-managed Google OAuth callback */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* iter-318b: Google sign-up — role picker for first-time Google users */}
          <Route path="/auth/google-signup" element={<GoogleSignup />} />
          <Route
            path="/"
            element={<HomeRoute />}
          />
          <Route
            path="/vendor/rates"
            element={
              <ProtectedRoute>
                <VendorRates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/chats"
            element={
              <ProtectedRoute>
                <VendorChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/boq"
            element={
              <ProtectedRoute>
                <VendorBOQ />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/tasks"
            element={
              <ProtectedRoute>
                <VendorTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/schedule"
            element={
              <ProtectedRoute>
                <VendorSchedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vendor-chats"
            element={
              <ProtectedRoute>
                <VendorChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <Leads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/dashboard"
            element={
              <ProtectedRoute>
                <ProjectDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Tasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor-quotes/compare/:requestId"
            element={
              <ProtectedRoute>
                <CompareVendorQuotes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activities"
            element={
              <ProtectedRoute>
                <Activities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activities"
            element={
              <ProtectedRoute>
                <AdminActivities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/active-sessions"
            element={
              <ProtectedRoute>
                <AdminActiveSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/data-health"
            element={
              <ProtectedRoute>
                <AdminDataHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <Payments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-budget"
            element={
              <ProtectedRoute>
                <AIBudgetEstimation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq-worksheets"
            element={
              <ProtectedRoute>
                <BOQWorksheets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq-worksheets/:worksheetId/edit"
            element={
              <ProtectedRoute allowedRoles={['owner', 'admin', 'staff']}>
                <BOQEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq-worksheets/:worksheetId/email"
            element={
              <ProtectedRoute>
                <BOQEmailPage />
              </ProtectedRoute>
            }
          />
          {/* iter-326c: Running Bills - iter-386u-x admin-only */}
          <Route
            path="/running-bills"
            element={
              <ProtectedRoute allowedRoles={['owner', 'admin', 'staff']}>
                <RunningBillsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/running-bills/:billId"
            element={
              <ProtectedRoute allowedRoles={['owner', 'admin', 'staff']}>
                <RunningBillsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq-comments"
            element={
              <ProtectedRoute>
                <BOQComments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations"
            element={
              <ProtectedRoute>
                <Quotations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/quotations/create"
            element={
              <ProtectedRoute>
                <VendorQuotationCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/materials"
            element={
              <ProtectedRoute>
                <MaterialsNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute>
                <PurchaseOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute>
                <Schedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <FullCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gantt"
            element={
              <ProtectedRoute>
                <GanttChart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor-catalogues"
            element={
              <ProtectedRoute>
                <VendorCatalogues />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['owner', 'admin', 'staff']}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/boq-categories"
            element={
              <ProtectedRoute>
                <BOQCategoryLibrary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/sponsors"
            element={
              <ProtectedRoute>
                <SponsorAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/sponsors/:id/analytics"
            element={
              <ProtectedRoute>
                <SponsorAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/ai-presets"
            element={
              <ProtectedRoute>
                <AIPresetLibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/pdf-templates"
            element={
              <ProtectedRoute>
                <PDFTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/email-templates"
            element={
              <ProtectedRoute>
                <EmailTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <NotificationPreferences />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/settings"
            element={
              <ProtectedRoute>
                <VendorSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vendor-rates"
            element={
              <ProtectedRoute>
                <AdminVendorRates />
              </ProtectedRoute>
            }
          />
          {/* Client-specific routes */}
          <Route
            path="/client/quotations"
            element={
              <ProtectedRoute>
                <ClientQuotations />
              </ProtectedRoute>
            }
          />
          {/* Admin view - Client Section */}
          <Route
            path="/client/quotations-sent"
            element={
              <ProtectedRoute>
                <ClientQuotationsSent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/boq"
            element={
              <ProtectedRoute>
                <ClientBOQ />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/tasks"
            element={
              <ProtectedRoute>
                <ClientTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/approvals"
            element={
              <ProtectedRoute>
                <ClientApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />
          {/* Admin view - Vendor Section */}
          <Route
            path="/vendor/quotations-received"
            element={
              <ProtectedRoute>
                <VendorQuotationsReceived />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/running-bills"
            element={
              <ProtectedRoute>
                <VendorRunningBills />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing-manager"
            element={
              <ProtectedRoute allowedRoles={["owner"]}>
                <AdminBillingManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/payment-history"
            element={
              <ProtectedRoute>
                <ClientPaymentHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/payment-history"
            element={
              <ProtectedRoute>
                <VendorPaymentHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/project-ledger"
            element={
              <ProtectedRoute>
                <VendorProjectLedgerIndex />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/project-ledger/:projectId"
            element={
              <ProtectedRoute>
                <VendorProjectLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors/compare"
            element={
              <ProtectedRoute>
                <VendorCompare />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <ClientsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors-labours"
            element={
              <ProtectedRoute>
                <VendorsLabourList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor-submissions"
            element={
              <ProtectedRoute>
                <VendorSubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors/:vendorId?"
            element={
              <ProtectedRoute>
                <VendorDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/performance"
            element={
              <ProtectedRoute>
                <PerformanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients-directory/:clientId?"
            element={
              <ProtectedRoute>
                <ClientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-accounts"
            element={
              <ProtectedRoute>
                <ProjectAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/estimators/wardrobe/:estimateId?"
            element={
              <ProtectedRoute>
                <WardrobeCalculator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/estimators/kitchen/:estimateId?"
            element={
              <ProtectedRoute>
                <WardrobeCalculator initialProductType="kitchen" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/estimators/compare"
            element={
              <ProtectedRoute>
                <EstimateCompare />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/discount-rules"
            element={
              <ProtectedRoute>
                <DiscountRulesPage />
              </ProtectedRoute>
            }
          />
          {/* iter-386u-g — task #4: catch-all route redirects any missing page back to /. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <PlanUpgradeModal />
        <CreditBalanceChip showChip={false} />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
      </WebSocketProvider>
    </ThemeProvider>
  );
}

export default App;
export { API };